export const DEFAULT_FILTER = {
    text        : '',
    messagetype : null,
    invoicetype : null,
    customer    : null,
    confirmed   : false,
    unconfirmed : true,
    uploads     : true,
    mails       : true,
    success     : true,
    warning     : true,
    removed     : false,
    error       : true,
    lastDay     : false,
};

function textIncludes(value, search) {
    if (!value || !search) return false;
    return String(value).toLowerCase().includes(search.toLowerCase());
}

/**
 * Apply inbox filter criteria to a list of import entries.
 *
 * Quality filter uses ANY-match semantics: an import is included when at least
 * one of its attachments matches the selected quality levels.  The previous
 * inline implementation used ALL-match (break on first mismatch), which caused
 * emails with mixed-quality attachments to be hidden incorrectly.
 *
 * @param {Array}  messages - raw import list from the backend
 * @param {Object} filter   - current filter state (matches DEFAULT_FILTER shape)
 * @returns {Array} filtered import list
 */
export function applyFilter(messages, filter) {
    if (!messages) return [];
    const filtered = [];

    for (const imported of messages) {
        // 1. Source type (EMAIL / MANUAL)
        const typeId   = imported.type && imported.type.id;
        const isEmail  = typeId === 'EMAIL';
        const isManual = typeId === 'MANUAL';

        if (isEmail  && !filter.mails)   continue;
        if (isManual && !filter.uploads) continue;
        if (!isEmail && !isManual)        continue;

        // 2. Customer filter (used in "Alle Meldungen" mode)
        if (filter.customer) {
            const importCustomerId = imported.customer && imported.customer.id;
            if (importCustomerId !== filter.customer.id) continue;
        }

        // 3. Message type filter — any attachment must match
        if (filter.messagetype) {
            const msgs = imported.messages || [];
            if (!msgs.find(m => m.type && m.type.id === filter.messagetype.id)) continue;
        }

        // 4. Invoice type filter — any attachment must match (was never applied before)
        if (filter.invoicetype) {
            const msgs = imported.messages || [];
            if (!msgs.find(m => m.invoiceType && m.invoiceType.id === filter.invoicetype.id)) continue;
        }

        // 5. Text search in subject or body
        if (filter.text && filter.text.length > 0) {
            if (!textIncludes(imported.subject, filter.text) && !textIncludes(imported.body, filter.text)) continue;
        }

        // 6. Quality filter — ANY attachment must match, not ALL
        const msgs = imported.messages || [];
        if (msgs.length > 0) {
            const qualityMatch = msgs.some(msg => {
                const qId = msg.quality && msg.quality.id;
                if (filter.success && qId === 1) return true;
                if (filter.warning && qId === 2) return true;
                if (filter.error   && qId === 3) return true;
                return false;
            });
            if (!qualityMatch) continue;
        }

        filtered.push(imported);
    }

    return filtered;
}

/**
 * Returns true when the filter differs from the default state in any way that
 * changes which imports are visible.  Used to show/hide the "clear filter" button.
 *
 * Previously missing: success / warning / error quality checks.
 *
 * @param {Object} filter - current filter state
 * @returns {boolean}
 */
export function isFilterActive(filter) {
    return (
        JSON.stringify(filter.messagetype) !== JSON.stringify(DEFAULT_FILTER.messagetype) ||
        JSON.stringify(filter.invoicetype) !== JSON.stringify(DEFAULT_FILTER.invoicetype) ||
        filter.customer    !== DEFAULT_FILTER.customer    ||
        filter.confirmed   !== DEFAULT_FILTER.confirmed   ||
        filter.unconfirmed !== DEFAULT_FILTER.unconfirmed ||
        filter.uploads     !== DEFAULT_FILTER.uploads     ||
        filter.removed     !== DEFAULT_FILTER.removed     ||
        filter.mails       !== DEFAULT_FILTER.mails       ||
        filter.success     !== DEFAULT_FILTER.success     ||
        filter.warning     !== DEFAULT_FILTER.warning     ||
        filter.error       !== DEFAULT_FILTER.error       ||
        filter.lastDay     !== DEFAULT_FILTER.lastDay     ||
        Boolean(filter.text && filter.text.length > 0)
    );
}
